import { useSortable } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import { type FC, type PropsWithChildren } from "react";
import { ColorButton, TriggerButton } from "widgets";
import { getCorrectedColor } from "widgets/utils";

import { ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS } from "@/settings/settings.ts";
import { EllipsedContent } from "@/ui-modules/core/grid/cells/core/EllipsedContent.tsx";

import { type ColumnAnnotation } from "../../graphql.js";
import { type SelectedDataSeries } from "../../redux/modules/dataset-viewer.js";
import { SortableDragHandleBasicWrapper } from "../core/forms/Sortable.js";
import style from "../core/forms/styles/Sortable.module.scss";
import { DataSeriesPropertiesDialog } from "./DataSeriesPropertiesDialog.js";

export interface IDataSeriesItemProps {
  dataSeries: SelectedDataSeries;
  isActive?: boolean;
  splitConditions: ColumnAnnotation[];
  handleRemoveItem: (item: SelectedDataSeries) => void;
  handleChangeColor: (item: SelectedDataSeries, color: string) => void;
  handleChangeHidden: (dataSeries: SelectedDataSeries) => void;
  handleSplitConditionSelected: (splitCondition: ColumnAnnotation, dataSeries: SelectedDataSeries) => void;
  isOpen?: (isOpen: boolean) => void;
}

const DataSeriesItem: FC<PropsWithChildren<IDataSeriesItemProps>> = (props) => {
  const { dataSeries, isActive } = props;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.dataSeries.name,
  });

  const dragStyle = transform
    ? { transform: `translate3d( 0,${transform.y}px, 0)`, transition, zIndex: isActive ? 2 : 1 }
    : undefined;
  if (!dataSeries) return null;

  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={dragStyle}>
      <div className={style.sortable}>
        <div className={style.sortable_container} style={{ flex: "1 1 auto" }}>
          <SortableDragHandleBasicWrapper />
          <ColorButton
            defaultColor={dataSeries.color ?? undefined}
            onApply={(color) => props.handleChangeColor(dataSeries, color)}
            onOpenChange={(isOpen) => props.isOpen?.(isOpen)}
          />
          <EllipsedContent className={style.label} tooltip={dataSeries.name ?? undefined}>
            {dataSeries.name}
          </EllipsedContent>
        </div>
        {!dataSeries.isLoading && (
          <div className={clsx(style.sortable_container, style.reduced_column_gap)} style={{ flex: "0 0 auto" }}>
            <DataSeriesPropertiesDialog
              dataSeries={dataSeries}
              splitConditions={props.splitConditions}
              handleChangeHidden={props.handleChangeHidden}
              handleSplitConditionSelected={props.handleSplitConditionSelected}
            />
            <TriggerButton
              onPress={() => props.handleChangeHidden(dataSeries)}
              icon={dataSeries.hidden ? "visibility_off" : "visibility"}
              label={dataSeries.hidden ? "Show This Data Series" : "Hide This Data Series"}
            />
            <TriggerButton
              onPress={() => props.handleRemoveItem(dataSeries)}
              label="Remove This Data Series"
              icon="close"
            />
          </div>
        )}
      </div>
      {(Array.isArray(props.children) ? props.children.length > 0 : props.children) && (
        <span style={{ marginLeft: 24, display: "flex", flexDirection: "column", rowGap: 3, marginTop: 3 }}>
          {props.children}
        </span>
      )}
    </div>
  );
};

const DataSeriesSplitItem: FC<PropsWithChildren<IDataSeriesItemProps>> = (props) => {
  const { dataSeries } = props;

  const dataSeriesSplitByCondition: ColumnAnnotation =
    dataSeries.splitParent &&
    props.splitConditions.find((sc: ColumnAnnotation) => sc.field === dataSeries.splitByCondition);

  const dataSeriesItemLabel: string = dataSeriesSplitByCondition
    ? `${dataSeries.splitValue} (${dataSeriesSplitByCondition.name})`
    : dataSeries.name;

  if (!dataSeries) return null;

  return (
    <div className={style.sortable}>
      <div className={style.sortable_container} style={{ flex: "1 1 auto" }}>
        <ColorButton
          defaultColor={dataSeries.color ?? undefined}
          onApply={(color) => props.handleChangeColor(dataSeries, color)}
          onOpenChange={props.isOpen}
          colorCorrectionFunc={(color: string) => getCorrectedColor(color, ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS)} // show the same (or a similar) color on the button like used in the line and bar chart
        />
        <EllipsedContent className={style.label} style={{ fontSize: "90%" }} tooltip={dataSeriesItemLabel}>
          {dataSeriesItemLabel}
        </EllipsedContent>
      </div>
      {!dataSeries.isLoading && (
        <div className={clsx(style.sortable_container, style.reduced_column_gap)} style={{ flex: "0 0 auto" }}>
          <TriggerButton
            onPress={() => props.handleChangeHidden(dataSeries)}
            icon={dataSeries.hidden ? "visibility_off" : "visibility"}
            label={dataSeries.hidden ? "Show This Data Series" : "Hide This Data Series"}
          />
          <TriggerButton
            onPress={() => props.handleRemoveItem(dataSeries)}
            label="Remove This Data Series"
            icon="close"
          />
        </div>
      )}
    </div>
  );
};
export { DataSeriesItem, DataSeriesSplitItem };
